import { defineStore } from 'pinia';
import { useConnectionStore } from '@/stores/connection';
import type { Query, Mutation, SaveVenueMutationInput } from '@/generated/graphql';
import * as Venue from '@/types/Venue';
import type RequestResult from '@/types/RequestResult';
import * as apolloCore from '@apollo/client/core';

const { gql } = apolloCore;

const VENUE_DETAILS_FRAGMENT = gql`
  fragment VenueDetails on Venue {
    id
    name
    replyTo
    url
    currency
    locale
    logoUrl
    timezone
    salesTax
    processingFee
    processingFeePercentage
    includeCcFee
    includeConvenienceFee
    includeTaxFee
    address {
      city
      country
      postalCode
      state
      street1
      street2
    }
    rooms {
      id
      name
    }
    logoId
    logo {
      id
      name
      url
    }
  }
`;

const ALL_VENUES_QUERY = gql`
  query allVenues {
    app {
      venues {
        ...VenueDetails
      }
    }
  }
  ${VENUE_DETAILS_FRAGMENT}
`;

const SAVE_VENUE_MUTATION = gql`
  mutation saveVenueMutation($input: SaveVenueMutationInput!) {
    app {
      saveVenue(input: $input) {
        clientMutationId
        errors
        venue {
          ...VenueDetails
        }
      }
    }
  }
  ${VENUE_DETAILS_FRAGMENT}
`;

const DELETE_VENUE_MUTATION = gql`
  mutation DeleteVenueMutation($id: ID!) {
    app {
      deleteVenue(input: { id: $id }) {
        errors
      }
    }
  }
`;
interface VenueState {
  venues: Venue.Venue[];
  venueLoading: boolean;
}

export const useVenueStore = defineStore('venue', {
  state: (): VenueState => ({
    venues: [],
    venueLoading: true,
  }),

  actions: {
    async fetchAllVenues() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.venueLoading = true;
      try {
        const { data } = await apiClient.query<Query>({
          query: ALL_VENUES_QUERY,
          fetchPolicy: 'network-only',
        });
        this.venues = (data.app?.venues || []).map(Venue.fromGql);
      } finally {
        this.venueLoading = false;
      }
    },

    async saveVenue(payload: SaveVenueMutationInput): Promise<RequestResult<Venue.Venue>> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.venueLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: SAVE_VENUE_MUTATION,
          variables: { input: payload },
        });

        if (data?.app?.saveVenue?.errors) {
          return { errors: data?.app.saveVenue.errors };
        }

        if (data?.app?.saveVenue?.venue) {
          return { result: Venue.fromGql(data?.app?.saveVenue?.venue) };
        }
        return {};
      } finally {
        this.venueLoading = false;
      }
    },

    async deleteVenue(id: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.venueLoading = true;

      try {
        await apiClient.mutate<Mutation>({
          mutation: DELETE_VENUE_MUTATION,
          variables: {
            id,
          },
        });
      } finally {
        this.venueLoading = false;
      }
    },
  },
});
